import { useState, useRef, useCallback, useEffect } from 'react';
import { getUnresolvedConflicts } from '@/lib/syncQueue';

/**
 * Hook pour éviter les doublons visuels quand une page combine :
 * 1. Optimistic UI (création hors-ligne → affichage immédiat avec ID temporaire)
 * 2. Realtime (INSERT reçu depuis le serveur après replay)
 * 3. Replay de la queue (l'élément réapparaît via la synchro)
 *
 * Principe :
 * - Quand on crée un élément hors-ligne, on enregistre son ID temporaire
 *   dans un Set local (`pendingOfflineIds`).
 * - Quand un événement Realtime arrive, on filtre les éléments qui sont
 *   déjà dans ce Set (l'UI les affiche déjà en mode optimiste).
 * - Quand la synchro se termine (plus de conflits), on vide le Set
 *   pour que les données serveur remplacent les données optimistes.
 *
 * Usage :
 *   const { registerOfflineItem, filterRealtimeDuplicates, clearOptimisticCache } = useOptimisticRealtime();
 *
 *   // À la création hors-ligne :
 *   const tempId = `pending-${Date.now()}`;
 *   setLocalData((prev) => [...prev, { id: tempId, ...formData, _optimistic: true }]);
 *   registerOfflineItem(tempId);
 *
 *   // Dans le handler Realtime :
 *   channel.on('INSERT', (payload) => {
 *     if (filterRealtimeDuplicates(payload.new.id)) return; // Déjà affiché en mode optimiste
 *     setLocalData((prev) => [...prev, payload.new]);
 *   });
 */
export function useOptimisticRealtime() {
  const pendingOfflineIds = useRef<Set<string>>(new Set());
  const [pendingCount, setPendingCount] = useState(0);

  // Enregistrer un ID créé hors-ligne (optimistic)
  const registerOfflineItem = useCallback((tempId: string) => {
    pendingOfflineIds.current.add(tempId);
    setPendingCount(pendingOfflineIds.current.size);
  }, []);

  // Enregistrer plusieurs IDs
  const registerOfflineItems = useCallback((tempIds: string[]) => {
    for (const id of tempIds) {
      pendingOfflineIds.current.add(id);
    }
    setPendingCount(pendingOfflineIds.current.size);
  }, []);

  // Vérifier si un ID entrant (depuis Realtime) est déjà affiché en optimistic
  // Retourne true si l'élément doit être filtré (déjà présent)
  const filterRealtimeDuplicates = useCallback((incomingId: string): boolean => {
    return pendingOfflineIds.current.has(incomingId);
  }, []);

  // Remplacer un ID temporaire par l'ID réel (après confirmation serveur via Realtime)
  const replaceOptimisticId = useCallback((tempId: string, realId: string) => {
    if (pendingOfflineIds.current.has(tempId)) {
      pendingOfflineIds.current.delete(tempId);
      // On ne garde plus l'ID réel dans le Set — le serveur fait foi
      setPendingCount(pendingOfflineIds.current.size);
    }
  }, []);

  // Vider tout le cache optimiste (ex: après une synchro complète)
  const clearOptimisticCache = useCallback(() => {
    pendingOfflineIds.current.clear();
    setPendingCount(0);
  }, []);

  // Vider automatiquement quand il n'y a plus de conflits
  useEffect(() => {
    const interval = setInterval(() => {
      const conflicts = getUnresolvedConflicts();
      if (conflicts.length === 0 && pendingOfflineIds.current.size > 0) {
        // Plus de conflits = la synchro est terminée
        // On vide le cache pour que les données serveur prennent le relais
        pendingOfflineIds.current.clear();
        setPendingCount(0);
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return {
    registerOfflineItem,
    registerOfflineItems,
    filterRealtimeDuplicates,
    replaceOptimisticId,
    clearOptimisticCache,
    pendingOptimisticCount: pendingCount,
  };
}